
export const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};

export const VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 30, max: 30 },
  facingMode: 'user',
};

export type MediaFailure =
  | 'denied'
  | 'no-device'
  | 'in-use'
  | 'insecure-context'
  | 'unsupported'
  | 'unknown';

export class MediaError_ extends Error {
  constructor(
    readonly reason: MediaFailure,
    message: string,
  ) {
    super(message);
    this.name = 'MediaError';
  }
}

function classify(err: unknown): MediaError_ {
  if (!isSecureContext) {
    return new MediaError_(
      'insecure-context',
      'Camera access needs a secure connection. Open this page over HTTPS.',
    );
  }
  const name = err instanceof DOMException ? err.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return new MediaError_(
        'denied',
        'Camera and microphone permission was blocked. Allow access in your browser’s address bar, then try again.',
      );
    case 'NotFoundError':
    case 'OverconstrainedError':
      return new MediaError_('no-device', 'No camera or microphone was found on this device.');
    case 'NotReadableError':
    case 'AbortError':
      return new MediaError_(
        'in-use',
        'Your camera or microphone is already in use by another app. Close it and try again.',
      );
    default:
      return new MediaError_('unknown', 'Could not start your camera and microphone.');
  }
}

export async function getLocalMedia(): Promise<MediaStream> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    throw new MediaError_(
      'unsupported',
      'This browser cannot make video calls. Try a recent Chrome, Firefox, Edge or Safari.',
    );
  }
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: AUDIO_CONSTRAINTS,
      video: VIDEO_CONSTRAINTS,
    });
  } catch (err) {
    throw classify(err);
  }
}

export const MAX_VIDEO_BITRATE = 600_000;

export async function capOutboundVideo(pc: RTCPeerConnection): Promise<void> {
  for (const sender of pc.getSenders()) {
    if (sender.track?.kind !== 'video') continue;
    const params = sender.getParameters();
    if (!params.encodings || params.encodings.length === 0) {
      params.encodings = [{}];
    }
    const encoding = params.encodings[0];
    if (!encoding) continue;
    encoding.maxBitrate = MAX_VIDEO_BITRATE;
    encoding.scaleResolutionDownBy = 1.5;
    try {
      await sender.setParameters(params);
    } catch {
    }
  }
}

export function stopStream(stream: MediaStream | null): void {
  for (const track of stream?.getTracks() ?? []) track.stop();
}
